import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Button,
  Tooltip,
  Chip,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  PageContainer,
  StyledCard,
  SectionHeader,
  PrimaryButton,
} from '../components/ui/StyledComponents'
import supabase from '../supabaseClient'
import { formatDistanceToNow } from 'date-fns'

function AdminClassesPage() {
  const [classes, setClasses] = useState([])
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    const fetchClasses = async () => {
      // For admin, we fetch all classes without teacher_id filter
      // Filter to only show classes with active teachers (or no teacher)
      const { data, error } = await supabase
        .from('classes')
        .select(`
          *,
          teachers:teacher_id (
            first_name,
            last_name,
            last_active,
            is_active
          )
        `)

      if (error) {
        console.error('Error fetching classes:', error)
      } else {
        setClasses(data || [])
      }
    }

    fetchClasses()
  }, [])

  const handleClassClick = (classId) => {
    navigate(`/class/${classId}`)
  }

  const handleManageClass = (classId) => {
    // Navigate to class management page
    navigate(`/admin/class/${classId}/manage`)
  }

  return (
    <PageContainer>
      <Grid container spacing={3}>
        <Grid 
          item 
          xs={12} 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 2, sm: 0 },
          }}
        >
          <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: 0 }}>
            All Classes
          </SectionHeader>
          <PrimaryButton
            variant="contained"
            onClick={() => navigate('/admin/management#class')}
            startIcon={<AddIcon />}
            fullWidth={isMobile}
            sx={{ 
              minWidth: { xs: '100%', sm: 'auto' },
            }}
          >
            Create New Class
          </PrimaryButton>
        </Grid>

        <Grid item xs={12}>
          <StyledCard sx={{ overflow: 'hidden' }}>
            <TableContainer sx={{ 
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
              },
            }}>
              <Table sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Class Name
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Teacher
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', md: 'table-cell' },
                    }}>
                      Shift
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {classes.length > 0 ? (
                    classes.map((classItem) => (
                      <TableRow key={classItem.id}>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          fontWeight: { xs: 500, sm: 400 },
                        }}>
                          {classItem.name}
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {classItem.teachers ? (
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                                  {classItem.teachers.first_name} {classItem.teachers.last_name}
                                </Typography>
                                {classItem.teachers.is_active === false && (
                                  <Chip
                                    label="Inactive"
                                    color="default"
                                    size="small"
                                    sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' }, height: { xs: '16px', sm: '18px' } }}
                                  />
                                )}
                              </Box>
                              {classItem.teachers.last_active && classItem.teachers.is_active !== false ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                    {formatDistanceToNow(new Date(classItem.teachers.last_active), { addSuffix: true })}
                                  </Typography>
                                  <Chip
                                    label={(() => {
                                      const daysSinceActive = Math.floor((new Date() - new Date(classItem.teachers.last_active)) / (1000 * 60 * 60 * 24));
                                      if (daysSinceActive === 0) return 'Active';
                                      if (daysSinceActive <= 3) return 'Recent';
                                      return 'Inactive';
                                    })()}
                                    color={(() => {
                                      const daysSinceActive = Math.floor((new Date() - new Date(classItem.teachers.last_active)) / (1000 * 60 * 60 * 24));
                                      if (daysSinceActive === 0) return 'success';
                                      if (daysSinceActive <= 3) return 'info';
                                      return 'warning';
                                    })()}
                                    size="small"
                                    sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' }, height: { xs: '14px', sm: '16px' } }}
                                  />
                                </Box>
                              ) : classItem.teachers.is_active === false ? null : (
                                <Chip
                                  label="Never Active"
                                  color="error"
                                  size="small"
                                  sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' }, height: { xs: '14px', sm: '16px' }, mt: 0.5 }}
                                />
                              )}
                              {/* Show shift on mobile */}
                              {isMobile && (
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                                  Shift: {classItem.shift || 'No shift Assigned'}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: 'text.secondary' }}>
                                No teacher assigned
                              </Typography>
                              {/* Show shift on mobile */}
                              {isMobile && (
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                                  Shift: {classItem.shift || 'No shift Assigned'}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', md: 'table-cell' },
                        }}>
                          {classItem.shift || 'No shift Assigned'}
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 1 },
                            alignItems: { xs: 'stretch', sm: 'center' },
                          }}>
                              <PrimaryButton
                                variant="contained"
                                onClick={() => handleClassClick(classItem.id)}
                                size="small"
                              fullWidth={isMobile}
                              sx={{ 
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: '100%', sm: 'auto' },
                              }}
                              >
                              {isMobile ? 'View' : 'View Details'}
                              </PrimaryButton>
                              <Tooltip title="Manage Class">
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<EditIcon />}
                                  onClick={() => handleManageClass(classItem.id)}
                                  size="small"
                                fullWidth={isMobile}
                                sx={{ 
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  minWidth: { xs: '100%', sm: 'auto' },
                                }}
                                >
                                  Edit
                                </Button>
                              </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 3 : 4} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        No classes to display
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </StyledCard>
        </Grid>
      </Grid>
    </PageContainer>
  )
}

export default AdminClassesPage 